/**
 * Stitch Review Page — issues #812 and #818, epic #802.
 *
 * A "human-in-the-loop" review UI for the FCG/BCG stitching pipeline.
 * Each unresolved or ambiguous API call site is rendered as a card
 * with the client call on the left and ranked candidate backend
 * endpoints on the right, including a per-candidate rationale
 * explaining why that route is a plausible match. The user can
 * accept, reject, change, mark external, or ask Claude to propose a
 * binding — and see the exact prompt before any AI call is made.
 */

import { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { SummaryCard } from '../components/stitching/SummaryCard';
import {
  api,
  type StitchReviewResponse,
  type StitchApiCallSite,
  type StitchCandidateExplanation,
  type StitchRouteEndpoint,
  type StitchRuleEntry,
  type StitchPromptPreview,
  type StitchEntry,
} from '../services/api';

interface ReviewRow {
  site: StitchApiCallSite;
  candidates: StitchCandidateExplanation[];
  bucket: 'unresolved' | 'ambiguous';
  ambiguityNote?: string;
}

/**
 * Issue #832: per-site batch result accumulated from the SSE stream.
 * Status drives the row's color in the results table and the filter
 * chip it belongs to.
 */
interface BatchResult {
  callSiteId: string;
  method: string;
  urlPattern: string;
  proposed: boolean;
  autoAccepted?: boolean;
  confidence?: number;
  rationale?: string;
  reason?: string;   // 'no-candidates' | 'model-declined' | 'parse-failure' | 'api-error' | 'rate-limit-exhausted' | 'below-threshold'
  detail?: string;
}

type BatchResultStatus = 'proposed' | 'below-threshold' | 'declined' | 'error';

function classifyBatchResult(r: BatchResult): BatchResultStatus {
  if (r.proposed && r.autoAccepted) return 'proposed';
  if (r.proposed && !r.autoAccepted) return 'below-threshold';
  if (r.reason === 'api-error' || r.reason === 'rate-limit-exhausted' || r.reason === 'parse-failure') return 'error';
  return 'declined';
}

interface CreateRuleForm {
  matchKind: 'wrapperChain' | 'urlPattern' | 'enclosingSymbol';
  matchPattern: string;
  targetKind: 'exact' | 'pathSuffix' | 'regex';
  targetPattern: string;
  targetMethod: string;
  confidence: number;
  note: string;
}

const DEFAULT_RULE_FORM: CreateRuleForm = {
  matchKind: 'wrapperChain',
  matchPattern: '',
  targetKind: 'exact',
  targetPattern: '',
  targetMethod: '',
  confidence: 0.9,
  note: '',
};

/** Shorten a deep file path for display — keep the trailing 3 segments. */
function prettyPath(p: string): string {
  const parts = p.split('/');
  return parts.slice(-3).join('/');
}

export function StitchReviewPage() {
  const { projectId } = useParams<{ projectId: string }>();
  const [data, setData] = useState<StitchReviewResponse | null>(null);
  const [routes, setRoutes] = useState<StitchRouteEndpoint[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [banner, setBanner] = useState<string | null>(null);

  // Per-row local state: which candidates has the user rejected, and
  // is the row busy on a network round-trip.
  const [rejected, setRejected] = useState<
    Record<string, Set<string /* routeId */>>
  >({});
  const [busyCallSiteId, setBusyCallSiteId] = useState<string | null>(null);

  // Issue #827 / #885: clickable card filter. Four buckets now — the
  // `unresolvable` bucket holds sites whose URL the tracer couldn't
  // resolve at all (distinct from `unresolved` which means we have a
  // URL but no confident route match).
  type CardFilter = 'all' | 'confident' | 'ambiguous' | 'unresolved' | 'unresolvable';
  const [cardFilter, setCardFilter] = useState<CardFilter>('all');

  // Issue #885: bucket-level batch-AI confirmation. Default false — no
  // AI call fires without an explicit user confirmation.
  const [showUnresolvableBatchModal, setShowUnresolvableBatchModal] =
    useState(false);

  // Issue #830: confident stitch sort order.
  type StitchSort = 'newest' | 'oldest' | 'alphabetical' | 'confidence' | 'source';
  const [stitchSort, setStitchSort] = useState<StitchSort>('newest');

  // Modal state.
  const [pickingForSite, setPickingForSite] = useState<StitchApiCallSite | null>(
    null,
  );
  const [routeFilter, setRouteFilter] = useState('');
  const [showRuleDialog, setShowRuleDialog] = useState(false);
  const [ruleForm, setRuleForm] = useState<CreateRuleForm>(DEFAULT_RULE_FORM);
  const [ruleError, setRuleError] = useState<string | null>(null);

  // Issue #829: batch auto-AI stitching state.
  const [showBatchModal, setShowBatchModal] = useState(false);
  const [batchRunning, setBatchRunning] = useState(false);
  const [batchId, setBatchId] = useState<string | null>(null);
  const [batchProgress, setBatchProgress] = useState<{
    current: number;
    total: number;
    proposed: number;
    declined: number;
    errors: number;
    belowThreshold: number;
    currentSite?: string;
    model?: string;
    done: boolean;
    cancelled: boolean;
    totalTokensUsed?: number;
  } | null>(null);
  // Issue #832: per-site results accumulated as SSE streams them.
  const [batchResults, setBatchResults] = useState<BatchResult[]>([]);

  // Issue #841: store EventSource in a ref so we can close it on unmount.
  const batchEventSourceRef = useRef<EventSource | null>(null);
  useEffect(() => {
    return () => {
      batchEventSourceRef.current?.close();
      batchEventSourceRef.current = null;
    };
  }, []);

  const [aiModal, setAiModal] = useState<{
    site: StitchApiCallSite;
    preview: StitchPromptPreview | null;
    loading: boolean;
    /** The actual proposed stitches from the AI — shown inline with Accept/Reject. */
    proposals: StitchEntry[] | null;
    /** Summary text for the decline case. */
    resultText: string | null;
    error: string | null;
    sending: boolean;
    /** Set to true after the user accepts a proposal — triggers reload. */
    accepted: boolean;
  } | null>(null);

  // Issue #864 Phase A: flow regeneration state.
  const [regenerating, setRegenerating] = useState(false);
  // Issue #864 Phase D: track whether stitches changed since last regen.
  const [stitchesDirty, setStitchesDirty] = useState(false);
  const regenTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const regenerateFlows = useCallback(async () => {
    if (!projectId || regenerating) return;
    setRegenerating(true);
    try {
      const result = await api.regenerateFlowsFromStitches(projectId);
      setBanner(`E2E flows regenerated — ${result.flowCount} flows updated`);
      setStitchesDirty(false);
    } catch (err: any) {
      setBanner(`Flow regeneration failed: ${err?.message ?? 'unknown error'}`);
    } finally {
      setRegenerating(false);
    }
  }, [projectId, regenerating]);

  // Issue #864 Phase D: debounced auto-regeneration after stitch changes.
  const scheduleRegeneration = useCallback(() => {
    setStitchesDirty(true);
    if (regenTimerRef.current) clearTimeout(regenTimerRef.current);
    regenTimerRef.current = setTimeout(() => void regenerateFlows(), 5000);
  }, [regenerateFlows]);

  // Cleanup timer on unmount.
  useEffect(() => {
    return () => {
      if (regenTimerRef.current) clearTimeout(regenTimerRef.current);
    };
  }, []);

  const reload = useCallback(async () => {
    if (!projectId) return;
    setLoading(true);
    setError(null);
    try {
      const [reviewJson, bcgJson] = await Promise.all([
        api.getStitchReview(projectId),
        api.getBackendCallGraph(projectId).catch(() => null),
      ]);
      setData(reviewJson);
      setRoutes(bcgJson?.backendCallGraph?.endpoints ?? []);
    } catch (e: any) {
      setError(e?.message ?? 'Failed to load review queue');
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    void reload();
  }, [reload]);

  useEffect(() => {
    if (!banner) return;
    const t = setTimeout(() => setBanner(null), 4500);
    return () => clearTimeout(t);
  }, [banner]);

  // Build the unified review queue: ambiguous first (they have the
  // most structural signal), then unresolved — with per-row local
  // rejections filtered out of the candidate list.
  const queue: ReviewRow[] = useMemo(() => {
    if (!data) return [];
    const rows: ReviewRow[] = [];
    // Issue #827: respect cardFilter when building the queue.
    if (cardFilter !== 'confident') {
      if (cardFilter === 'all' || cardFilter === 'ambiguous') {
        for (const group of data.ambiguousCandidates) {
          rows.push({
            site: group.site,
            candidates: group.candidates,
            bucket: 'ambiguous',
            ambiguityNote: group.rationale,
          });
        }
      }
      if (cardFilter === 'all' || cardFilter === 'unresolved') {
        for (const group of data.unresolvedCandidates) {
          rows.push({
            site: group.site,
            candidates: group.candidates,
            bucket: 'unresolved',
          });
        }
      }
    }
    return rows.map(row => ({
      ...row,
      candidates: row.candidates.filter(
        c => !rejected[row.site.callSiteId]?.has(c.route.routeId),
      ),
    }));
  }, [data, rejected, cardFilter]);

  // Issue #830: sorted confident stitches.
  const sortedStitches = useMemo(() => {
    const items = data?.allStitches ?? [];
    if (items.length === 0) return items;
    const copy = [...items];
    const sourcePriority: Record<string, number> = { user: 1, ai: 2, auto: 3 };
    switch (stitchSort) {
      case 'newest':
        return copy.sort((a, b) =>
          (b.createdAt ?? '').localeCompare(a.createdAt ?? ''),
        );
      case 'oldest':
        return copy.sort((a, b) =>
          (a.createdAt ?? '').localeCompare(b.createdAt ?? ''),
        );
      case 'alphabetical':
        return copy.sort((a, b) =>
          (a.callSite.urlPattern ?? '').localeCompare(b.callSite.urlPattern ?? ''),
        );
      case 'confidence':
        return copy.sort((a, b) => b.confidence - a.confidence);
      case 'source':
        return copy.sort(
          (a, b) =>
            (sourcePriority[a.source] ?? 9) - (sourcePriority[b.source] ?? 9),
        );
      default:
        return copy;
    }
  }, [data?.allStitches, stitchSort]);

  const filteredRoutes = useMemo(() => {
    if (!routeFilter.trim()) return routes.slice(0, 50);
    const q = routeFilter.toLowerCase();
    return routes
      .filter(
        r =>
          r.path.toLowerCase().includes(q) ||
          r.method.toLowerCase().includes(q) ||
          (r.handlerFunction ?? '').toLowerCase().includes(q),
      )
      .slice(0, 50);
  }, [routes, routeFilter]);

  // ─────────────────────────── actions ───────────────────────────

  const acceptCandidate = async (
    site: StitchApiCallSite,
    candidate: StitchCandidateExplanation,
  ) => {
    if (!projectId) return;
    setBusyCallSiteId(site.callSiteId);
    try {
      await api.bindStitch(
        projectId,
        site.callSiteKey as unknown as Record<string, string>,
        candidate.route.routeKey as unknown as Record<string, string>,
      );
      setBanner(
        `Bound ${site.method} ${site.urlPattern} → ${candidate.route.path}`,
      );
      await reload();
      scheduleRegeneration(); // Phase D: auto-regen
    } catch (e: any) {
      setBanner(`Bind failed: ${e?.message ?? 'unknown'}`);
    } finally {
      setBusyCallSiteId(null);
    }
  };

  const rejectCandidate = (
    site: StitchApiCallSite,
    candidate: StitchCandidateExplanation,
  ) => {
    setRejected(prev => {
      const next = { ...prev };
      const set = new Set(next[site.callSiteId] ?? []);
      set.add(candidate.route.routeId);
      next[site.callSiteId] = set;
      return next;
    });
  };

  const ignoreSite = async (site: StitchApiCallSite) => {
    if (!projectId) return;
    setBusyCallSiteId(site.callSiteId);
    try {
      await api.ignoreStitch(
        projectId,
        site.callSiteKey as unknown as Record<string, string>,
      );
      setBanner(`Marked ${site.method} ${site.urlPattern} as external`);
      await reload();
    } catch (e: any) {
      setBanner(`Ignore failed: ${e?.message ?? 'unknown'}`);
    } finally {
      setBusyCallSiteId(null);
    }
  };

  const openAIModal = async (site: StitchApiCallSite) => {
    if (!projectId) return;
    setAiModal({
      site,
      preview: null,
      loading: true,
      proposals: null,
      resultText: null,
      error: null,
      sending: false,
      accepted: false,
    });
    try {
      const preview = await api.previewStitchPrompt(projectId, site.callSiteId);
      setAiModal(m =>
        m ? { ...m, preview, loading: false } : m,
      );
    } catch (e: any) {
      setAiModal(m =>
        m ? { ...m, loading: false, error: e?.message ?? 'Preview failed' } : m,
      );
    }
  };

  const sendAIProposal = async () => {
    if (!projectId || !aiModal) return;
    setAiModal(m => (m ? { ...m, sending: true, error: null } : m));
    try {
      const result = await api.proposeAIStitches(projectId, {
        callSiteId: aiModal.site.callSiteId,
        candidatesPerSite: 5,
      });
      // Build a detailed decline message from the skip reasons.
      let declineText: string | null = null;
      if (result.proposed === 0 && result.skipReasons?.length) {
        const r = result.skipReasons[0];
        const reasonLabels: Record<string, string> = {
          'no-candidates': 'No plausible candidates',
          'model-declined': 'AI evaluated but declined',
          'model-ambiguous': 'AI found candidates equally plausible',
          'parse-failure': 'Response could not be parsed',
          'out-of-range': 'Model returned an invalid match',
          'api-error': 'LLM API call failed',
          'budget-cap': 'Proposal budget exhausted',
        };
        declineText = `${reasonLabels[r.reason] ?? r.reason}${r.detail ? `: ${r.detail}` : ''}`;
      } else if (result.proposed === 0) {
        declineText = 'AI declined to propose a binding for this call site.';
      }

      setAiModal(m =>
        m
          ? {
              ...m,
              sending: false,
              proposals: result.proposedStitches ?? [],
              resultText: declineText,
            }
          : m,
      );
      if (result.proposed === 0) {
        setBanner('AI declined to propose a binding');
      }
    } catch (e: any) {
      setAiModal(m =>
        m
          ? { ...m, sending: false, error: e?.message ?? 'AI proposer failed' }
          : m,
      );
    }
  };

  const acceptAIProposal = async (proposal: StitchEntry) => {
    if (!projectId) return;
    try {
      await api.bindStitch(
        projectId,
        proposal.callSite as unknown as Record<string, string>,
        proposal.route as unknown as Record<string, string>,
        `AI-proposed (confidence ${proposal.confidence}, rationale: ${proposal.rationale})`,
      );
      setBanner(
        `Accepted AI binding: ${proposal.callSite.method ?? '?'} ${proposal.callSite.urlPattern ?? '?'} → ${proposal.route.path ?? '?'}`,
      );
      setAiModal(m => (m ? { ...m, accepted: true } : m));
      await reload();
      scheduleRegeneration(); // Phase D: auto-regen
    } catch (e: any) {
      setBanner(`Accept failed: ${e?.message ?? 'unknown'}`);
    }
  };

  const rejectAIProposal = () => {
    setBanner('AI proposal rejected — call site remains unresolved.');
    setAiModal(null);
  };

  // ─────────────────────── rules dialog actions ───────────────────────

  const deleteRule = async (ruleId: string) => {
    if (!projectId) return;
    try {
      await api.deleteStitchRule(projectId, ruleId);
      setBanner('Rule deleted');
      await reload();
    } catch (e: any) {
      setBanner(`Delete rule failed: ${e?.message ?? 'unknown'}`);
    }
  };

  const createRule = async () => {
    if (!projectId) return;
    setRuleError(null);
    if (!ruleForm.matchPattern.trim() || !ruleForm.targetPattern.trim()) {
      setRuleError('match and target pattern are required');
      return;
    }
    if (ruleForm.targetKind === 'regex') {
      const p = ruleForm.targetPattern;
      if (/\(\.\*\)\+|\(\.\+\)\+|\([^)]*\+\)\+|\\1|\\2/.test(p)) {
        setRuleError(
          'Regex contains a nested quantifier or backreference — please simplify.',
        );
        return;
      }
      try {
        new RegExp(p);
      } catch {
        setRuleError('Invalid regex');
        return;
      }
    }
    try {
      await api.createStitchRule(projectId, {
        match: { kind: ruleForm.matchKind, pattern: ruleForm.matchPattern.trim() },
        target: {
          kind: ruleForm.targetKind,
          pattern: ruleForm.targetPattern.trim(),
          method: ruleForm.targetMethod.trim() || undefined,
        },
        confidence: ruleForm.confidence,
        note: ruleForm.note.trim() || undefined,
      });
      setBanner('Rule created');
      setShowRuleDialog(false);
      setRuleForm(DEFAULT_RULE_FORM);
      await reload();
    } catch (e: any) {
      setRuleError(e?.message ?? 'Failed to create rule');
    }
  };

  const pickFromBrowser = async (
    site: StitchApiCallSite,
    route: StitchRouteEndpoint,
  ) => {
    // Used by the "Change…" route browser modal.
    await acceptCandidate(site, {
      route,
      score: 0,
      breakdown: {
        methodMatch: true,
        pathOverlap: 0,
        pathLengthDiff: 0,
        suffixMatch: false,
        exactMatch: false,
      },
      rationale: 'manually selected from route browser',
    });
    setPickingForSite(null);
  };

  // ─────────────────────────── render ───────────────────────────

  if (loading) return <div className="p-6">Loading review queue…</div>;
  if (error) return <div className="p-6 text-red-600">Error: {error}</div>;
  if (!data) return <div className="p-6">No data</div>;

  if (data.state === 'no-analysis') {
    return (
      <div className="p-6 max-w-3xl mx-auto">
        <PageHeader projectId={projectId} />
        <div className="mt-6 p-6 border border-slate-200 dark:border-slate-700 rounded bg-slate-50 dark:bg-slate-800/50">
          <div className="text-sm font-semibold text-slate-700 dark:text-slate-200 mb-1">
            No E2E flow analysis yet
          </div>
          <div className="text-sm text-slate-500 dark:text-slate-400">
            Run the analyzer on this project to produce a frontend + backend
            call graph. Once analysis completes, return here to resolve any
            call sites that the auto-stitcher couldn't match to a route.
          </div>
        </div>
      </div>
    );
  }
  if (data.state === 'no-call-graphs') {
    return (
      <div className="p-6 max-w-3xl mx-auto">
        <PageHeader projectId={projectId} />
        <div className="mt-6 p-6 border border-amber-200 dark:border-amber-800 rounded bg-amber-50 dark:bg-amber-900/20">
          <div className="text-sm font-semibold text-amber-800 dark:text-amber-200 mb-1">
            Analysis exists, but predates the stitching pipeline
          </div>
          <div className="text-sm text-amber-800/80 dark:text-amber-200/80">
            This project's stored E2E flow diagram has no
            <code className="mx-1 px-1 rounded bg-amber-100 dark:bg-amber-900/40">
              frontendCallGraph
            </code>
            or
            <code className="mx-1 px-1 rounded bg-amber-100 dark:bg-amber-900/40">
              backendCallGraph
            </code>
            metadata. Re-run analysis to populate them — the auto-stitcher
            needs both to classify call sites.
          </div>
        </div>
      </div>
    );
  }

  // Issue #827: use allStitches for the confident count — it merges
  // auto + user + ai stitches from the backend.
  const confidentCount = data.allStitches?.length ?? (data.stitches.length + data.userStitches.length);
  const ambiguousCount = data.ambiguousCandidates.length;
  const unresolvedCount = data.unresolvedCandidates.length;
  // Issue #885: unresolvableCandidates is optional for back-compat.
  const unresolvableCandidates = data.unresolvableCandidates ?? [];
  const unresolvableCount = unresolvableCandidates.length;

  const toggleFilter = (f: CardFilter) => {
    setCardFilter(prev => (prev === f ? 'all' : f));
  };

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <PageHeader projectId={projectId} />

      {banner && (
        <div
          role="status"
          className="mt-4 px-4 py-2 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 text-sm text-blue-700 dark:text-blue-300 rounded"
        >
          {banner}
        </div>
      )}

      {/* Issue #864 Phase A: Regenerate flows button */}
      <div className="flex items-center gap-3 mt-4">
        <button
          data-testid="regenerate-flows-btn"
          onClick={() => void regenerateFlows()}
          disabled={regenerating}
          className="px-3 py-1.5 text-sm font-medium rounded-lg transition-colors bg-green-600 hover:bg-green-700 text-white disabled:opacity-50"
        >
          {regenerating ? 'Regenerating...' : 'Regenerate E2E Flows'}
        </button>
        {stitchesDirty && !regenerating && (
          <span className="text-xs text-amber-600 dark:text-amber-400">
            Stitch bindings changed — flows will auto-regenerate in a few seconds
          </span>
        )}
        {!stitchesDirty && !regenerating && (
          <span className="text-xs text-slate-400 dark:text-slate-500">
            API Endpoints and Callers pages update automatically from stitch bindings.
          </span>
        )}
      </div>

      <div className="grid grid-cols-4 gap-4 mt-4 mb-6">
        <SummaryCard
          label="Confident stitches"
          value={confidentCount}
          tone="green"
          active={cardFilter === 'confident'}
          onClick={() => toggleFilter('confident')}
          tooltip={
            <>
              <strong>URL call sites bound to a backend route with high confidence.</strong>
              <br />
              Source includes auto-stitched matches (<code>produceAutoStitches</code>{' '}
              in <code>autoStitcher.ts</code>), user-confirmed bindings, and
              accepted AI proposals. These are applied to the call-graph + flow
              diagrams automatically.
            </>
          }
        />
        <SummaryCard
          label="Ambiguous"
          value={ambiguousCount}
          tone="amber"
          active={cardFilter === 'ambiguous'}
          onClick={() => toggleFilter('ambiguous')}
          tooltip={
            <>
              <strong>Multiple candidate routes scored similarly.</strong>
              <br />
              The auto-stitcher found more than one plausible match and refused
              to pick one. Review the candidates in the list and accept the
              correct route; the rejected candidates are remembered so they
              don't resurface.
            </>
          }
        />
        <SummaryCard
          label="Unresolved"
          value={unresolvedCount}
          tone="red"
          active={cardFilter === 'unresolved'}
          onClick={() => toggleFilter('unresolved')}
          tooltip={
            <>
              <strong>URL call sites with no matching backend route.</strong>
              <br />
              The call site's URL didn't match any detected route by method +
              path. Either the endpoint is external (mark it as such), the
              backend wasn't analyzed (cross-repo setup), or the URL needs a
              rule. Try "Suggest with AI" to get a proposal.
            </>
          }
        />
        {/* Issue #885: fourth bucket — neutral slate tone because
            these aren't a failure. The URL is opaque to the static
            tracer, but AI or manual review typically can resolve it. */}
        <SummaryCard
          label="Opaque URLs"
          value={unresolvableCount}
          tone="slate"
          active={cardFilter === 'unresolvable'}
          onClick={() => toggleFilter('unresolvable')}
          tooltipAlign="right"
          tooltip={
            <>
              <strong>URLs the static tracer couldn't resolve.</strong>
              <br />
              <code>{'{dynamic}'}</code>, <code>{'{traced-via-call-graph}'}</code>,
              or <code>{'${ENV_VAR}'}</code>-shaped patterns. Not a failure —
              static analysis is unable to follow these deterministically. The
              AI proposer (#888) gets enriched context for these and can
              typically resolve a meaningful share.
            </>
          }
        />
      </div>

      {/* ─── Issue #827: Confident stitches list ─── */}
      {(cardFilter === 'all' || cardFilter === 'confident') && data.allStitches && data.allStitches.length > 0 && (
        <section data-testid="confident-section" className="mb-8">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-lg font-semibold text-slate-700 dark:text-slate-300">
              Confident stitches
              <span className="ml-2 text-sm font-normal text-slate-500">
                ({data.allStitches.length} bound)
              </span>
            </h2>
            {/* Issue #830: sort controls */}
            <div data-testid="stitch-sort" className="flex items-center gap-1 text-xs">
              <span className="text-slate-500 mr-1">Sort:</span>
              {([
                ['newest', 'Newest'],
                ['oldest', 'Oldest'],
                ['alphabetical', 'A-Z'],
                ['confidence', 'Confidence'],
                ['source', 'Source'],
              ] as const).map(([key, label]) => (
                <button
                  key={key}
                  data-testid={`sort-${key}`}
                  onClick={() => setStitchSort(key)}
                  className={`px-2 py-0.5 rounded border transition-colors ${
                    stitchSort === key
                      ? 'bg-primary text-primary-foreground border-primary'
                      : 'border-slate-200 dark:border-slate-700 hover:bg-slate-100 dark:hover:bg-slate-700'
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>
          <div className="space-y-3">
            {sortedStitches.map((stitch, i) => (
              <ConfidentStitchRow
                // #1074 D7: index-suffix makes the key unique when two stitches
                // share path/urlPattern/method/source (e.g. multiple call sites
                // in the same file mapping to the same endpoint).
                key={`${stitch.callSite.filePath}|${stitch.callSite.urlPattern}|${stitch.callSite.method}|${stitch.source}#${i}`}
                stitch={stitch}
                onRebind={() => {
                  // Synthesize a minimal ApiCallSite for the route browser
                  const site: StitchApiCallSite = {
                    callSiteId: `bound-${i}`,
                    callSiteKey: stitch.callSite as unknown as StitchApiCallSite['callSiteKey'],
                    filePath: stitch.callSite.filePath ?? '',
                    lineStart: 0,
                    lineEnd: 0,
                    urlPattern: stitch.callSite.urlPattern ?? '',
                    method: stitch.callSite.method ?? '',
                    library: '',
                    wrapperChain: [],
                    isDynamicEndpoint: false,
                  };
                  setPickingForSite(site);
                }}
                onUnbind={async () => {
                  if (!projectId) return;
                  try {
                    await api.unbindStitch(
                      projectId,
                      stitch.callSite as unknown as Record<string, string>,
                    );
                    setBanner(`Unbound ${stitch.callSite.method ?? '?'} ${stitch.callSite.urlPattern ?? '?'} — call site returned to unresolved.`);
                    await reload();
                    scheduleRegeneration(); // Phase D: auto-regen
                  } catch (e: any) {
                    setBanner(`Unbind failed: ${e?.message ?? 'unknown'}`);
                  }
                }}
              />
            ))}
            {/* All confident stitches are shown — no cap needed since
                rows are compact and the user needs to find specific
                stitches to verify/rebind/unbind them. */}
          </div>
        </section>
      )}

      {/* ─── Ambiguous + Unresolved review queue ─── */}
      {(cardFilter === 'all' || cardFilter === 'ambiguous' || cardFilter === 'unresolved') && (
        <section className="mb-8">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-lg font-semibold text-slate-700 dark:text-slate-300">
              Review queue
              <span className="ml-2 text-sm font-normal text-slate-500">
                ({queue.length} call site{queue.length === 1 ? '' : 's'} need attention)
              </span>
            </h2>
            {unresolvedCount > 0 && (
              <button
                data-testid="batch-resolve-button"
                onClick={() => setShowBatchModal(true)}
                disabled={batchRunning}
                className="px-3 py-1.5 text-xs font-medium bg-purple-600 text-white rounded hover:bg-purple-700 disabled:opacity-50"
              >
                {batchRunning ? 'Batch running...' : 'Resolve all with AI'}
              </button>
            )}
          </div>
          {queue.length === 0 ? (
            <div className="text-sm text-slate-500">
              Nothing to review — every call site in the latest analysis is
              either confidently matched or has been resolved.
            </div>
          ) : (
            <div className="space-y-4">
              {queue.slice(0, 50).map(row => (
                <ReviewCard
                  key={row.site.callSiteId}
                  row={row}
                  busy={busyCallSiteId === row.site.callSiteId}
                  onAccept={c => void acceptCandidate(row.site, c)}
                  onReject={c => rejectCandidate(row.site, c)}
                  onChange={() => setPickingForSite(row.site)}
                  onIgnore={() => void ignoreSite(row.site)}
                  onAskAI={() => void openAIModal(row.site)}
                />
              ))}
              {queue.length > 50 && (
                <div className="text-xs text-slate-500 text-center pt-2">
                  Showing first 50 of {queue.length}. Resolve these and reload
                  for the next batch.
                </div>
              )}
            </div>
          )}
        </section>
      )}

      {/* ─── Issue #885: Opaque URL bucket ─── */}
      {(cardFilter === 'all' || cardFilter === 'unresolvable') &&
        unresolvableCount > 0 && (
        <section
          data-testid="unresolvable-section"
          className="mb-8"
        >
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-lg font-semibold text-slate-700 dark:text-slate-300">
              Opaque URLs
              <span className="ml-2 text-sm font-normal text-slate-500">
                ({unresolvableCount} call site
                {unresolvableCount === 1 ? '' : 's'} — tracer couldn&apos;t
                see the URL; AI or manual review typically can)
              </span>
            </h2>
            <button
              data-testid="batch-unresolvable-ai-button"
              onClick={() => setShowUnresolvableBatchModal(true)}
              disabled={batchRunning}
              className="px-3 py-1.5 text-xs font-medium bg-purple-600 text-white rounded hover:bg-purple-700 disabled:opacity-50"
            >
              {batchRunning
                ? 'Batch running...'
                : 'Propose all with AI'}
            </button>
          </div>
          <div className="space-y-3">
            {unresolvableCandidates.slice(0, 50).map(group => (
              <UnresolvableCard
                key={group.site.callSiteId}
                site={group.site}
                reason={group.reason}
                busy={busyCallSiteId === group.site.callSiteId}
                onPickRoute={() => setPickingForSite(group.site)}
                onIgnore={() => void ignoreSite(group.site)}
                onAskAI={() => void openAIModal(group.site)}
              />
            ))}
            {unresolvableCount > 50 && (
              <div className="text-xs text-slate-500 text-center pt-2">
                Showing first 50 of {unresolvableCount}.
              </div>
            )}
          </div>
        </section>
      )}

      {/* Issue #885: cost-estimate confirmation before batch AI fires.
          Default closed — no AI call without an explicit confirm. */}
      {showUnresolvableBatchModal && (
        <div
          data-testid="unresolvable-batch-modal"
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
          onClick={() => setShowUnresolvableBatchModal(false)}
        >
          <div
            className="max-w-md w-full mx-4 bg-white dark:bg-slate-900 rounded shadow-xl border border-slate-200 dark:border-slate-700 p-5"
            onClick={e => e.stopPropagation()}
          >
            <h3 className="text-sm font-semibold mb-2">
              Propose stitches for {unresolvableCount} opaque-URL call
              site{unresolvableCount === 1 ? '' : 's'}?
            </h3>
            <p className="text-xs text-slate-600 dark:text-slate-400 mb-1">
              AI will be invoked once per call site. Each call is roughly
              ~1,500 input tokens + ~300 output tokens; the exact cost
              depends on your configured model.
            </p>
            <p className="text-xs text-slate-600 dark:text-slate-400 mb-3">
              Rough estimate: ~{unresolvableCount * 1500} input +{' '}
              ~{unresolvableCount * 300} output tokens total. Proposals
              are staged for your review — no stitch is written until
              you accept it.
            </p>
            <div className="flex items-center justify-end gap-2">
              <button
                data-testid="unresolvable-batch-cancel"
                onClick={() => setShowUnresolvableBatchModal(false)}
                className="px-3 py-1.5 text-xs font-medium bg-slate-200 dark:bg-slate-700 text-slate-700 dark:text-slate-300 rounded"
              >
                Cancel
              </button>
              <button
                data-testid="unresolvable-batch-confirm"
                onClick={() => {
                  setShowUnresolvableBatchModal(false);
                  // Reuse the existing batch-SSE modal path: flipping
                  // showBatchModal opens the same batch flow that the
                  // review queue uses. This runs over ALL unresolved
                  // sites by default — the batch endpoint itself is
                  // responsible for scoping. See #829/#849 for the
                  // batch implementation.
                  setShowBatchModal(true);
                }}
                className="px-3 py-1.5 text-xs font-medium bg-purple-600 text-white rounded"
              >
                Run AI on {unresolvableCount} sites
              </button>
            </div>
          </div>
        </div>
      )}

      <section>
        <div className="flex items-center justify-between mb-2">
          <h2 className="text-lg font-semibold text-slate-700 dark:text-slate-300">
            Active rules
          </h2>
          <button
            onClick={() => {
              setRuleForm(DEFAULT_RULE_FORM);
              setRuleError(null);
              setShowRuleDialog(true);
            }}
            className="px-3 py-1.5 text-xs font-medium bg-blue-600 text-white rounded hover:bg-blue-700"
          >
            Create rule
          </button>
        </div>
        {data.rules.length === 0 ? (
          <div className="text-sm text-slate-500">No rules defined.</div>
        ) : (
          <ul className="divide-y divide-slate-200 dark:divide-slate-700 border border-slate-200 dark:border-slate-700 rounded">
            {data.rules.map((rule: StitchRuleEntry) => (
              <li key={rule.id} className="p-3 flex items-center justify-between">
                <div className="text-sm">
                  <div className="font-mono">
                    {rule.match.kind}:{rule.match.pattern} → {rule.target.kind}:{rule.target.pattern}
                    {rule.target.method && (
                      <span className="ml-2 text-xs text-slate-500">
                        [{rule.target.method}]
                      </span>
                    )}
                  </div>
                  {rule.note && (
                    <div className="text-xs text-slate-500 mt-0.5">{rule.note}</div>
                  )}
                </div>
                <button
                  onClick={() => void deleteRule(rule.id)}
                  className="px-2 py-1 text-xs text-red-600 hover:text-red-700"
                >
                  Delete
                </button>
              </li>
            ))}
          </ul>
        )}
      </section>

      {pickingForSite && (
        <RouteBrowserModal
          site={pickingForSite}
          routes={filteredRoutes}
          filter={routeFilter}
          onFilterChange={setRouteFilter}
          onClose={() => setPickingForSite(null)}
          onPick={r => void pickFromBrowser(pickingForSite, r)}
        />
      )}

      {showRuleDialog && (
        <CreateRuleDialog
          form={ruleForm}
          setForm={setRuleForm}
          error={ruleError}
          onCancel={() => setShowRuleDialog(false)}
          onSubmit={() => void createRule()}
        />
      )}

      {aiModal && (
        <AIProposeModal
          site={aiModal.site}
          preview={aiModal.preview}
          loading={aiModal.loading}
          sending={aiModal.sending}
          proposals={aiModal.proposals}
          resultText={aiModal.resultText}
          accepted={aiModal.accepted}
          error={aiModal.error}
          onClose={() => setAiModal(null)}
          onSend={() => void sendAIProposal()}
          onAccept={p => void acceptAIProposal(p)}
          onReject={rejectAIProposal}
        />
      )}

      {/* ─── Issue #829: Batch auto-AI stitching modal ─── */}
      {showBatchModal && (
        <BatchResolveModal
          unresolvedCount={cardFilter === 'ambiguous' ? ambiguousCount : unresolvedCount}
          filterLabel={cardFilter === 'ambiguous' ? 'ambiguous' : 'unresolved'}
          batchProgress={batchProgress}
          batchResults={batchResults}
          onStart={(opts) => {
            setBatchRunning(true);
            // #832: fresh batch — clear any results from a prior run.
            setBatchResults([]);
            setBatchProgress({
              current: 0,
              total: 0,
              proposed: 0,
              declined: 0,
              errors: 0,
              belowThreshold: 0,
              done: false,
              cancelled: false,
            });

            // Issue #844: pass current card filter to the batch endpoint.
            const batchFilter =
              cardFilter === 'ambiguous' ? 'ambiguous'
                : cardFilter === 'unresolved' ? 'unresolved'
                  : 'all';

            const params = new URLSearchParams({
              confidenceThreshold: String(opts.confidenceThreshold),
              maxProposals: String(opts.maxProposals),
              pacingDelayMs: String(opts.pacingDelayMs),
              includeAmbiguous: String(opts.includeAmbiguous),
              filter: batchFilter,
            });

            // Issue #841: close any previous EventSource before opening a new one.
            batchEventSourceRef.current?.close();
            const eventSource = new EventSource(
              `/api/projects/${projectId}/stitches/propose/batch?${params}`,
            );
            batchEventSourceRef.current = eventSource;

            eventSource.addEventListener('started', (e) => {
              const d = JSON.parse(e.data);
              setBatchId(d.batchId);
              setBatchProgress(p => p ? { ...p, total: d.total, model: d.model } : p);
            });

            eventSource.addEventListener('progress', (e) => {
              const d = JSON.parse(e.data);
              setBatchProgress(p => p ? {
                ...p,
                current: d.current,
                total: d.total,
                currentSite: `${d.method} ${d.urlPattern}`,
              } : p);
            });

            eventSource.addEventListener('result', (e) => {
              const d = JSON.parse(e.data) as BatchResult;
              // #832: keep the full result so the completion view can
              // show which sites declined and why, not just a count.
              setBatchResults(prev => [...prev, d]);
              setBatchProgress(p => {
                if (!p) return p;
                return {
                  ...p,
                  proposed: p.proposed + (d.proposed && d.autoAccepted ? 1 : 0),
                  declined: p.declined + (!d.proposed ? 1 : 0),
                  belowThreshold: p.belowThreshold + (d.proposed && !d.autoAccepted ? 1 : 0),
                };
              });
            });

            eventSource.addEventListener('rate-limited', (e) => {
              const d = JSON.parse(e.data);
              setBatchProgress(p => p ? { ...p, currentSite: d.message } : p);
            });

            eventSource.addEventListener('complete', (e) => {
              const d = JSON.parse(e.data);
              setBatchRunning(false);
              setBatchProgress(p => p ? {
                ...p,
                proposed: d.proposed,
                declined: d.declined,
                errors: d.errors,
                belowThreshold: d.belowThreshold,
                totalTokensUsed: d.totalTokensUsed,
                done: true,
              } : p);
              eventSource.close();
              batchEventSourceRef.current = null;
              void reload();
            });

            eventSource.addEventListener('cancelled', (e) => {
              const d = JSON.parse(e.data);
              setBatchRunning(false);
              setBatchProgress(p => p ? {
                ...p,
                proposed: d.proposed,
                declined: d.declined,
                errors: d.errors,
                belowThreshold: d.belowThreshold,
                totalTokensUsed: d.totalTokensUsed,
                done: true,
                cancelled: true,
              } : p);
              eventSource.close();
              batchEventSourceRef.current = null;
              void reload();
            });

            // Issue #851: show error state instead of silently marking done.
            eventSource.onerror = () => {
              setBatchRunning(false);
              setBatchProgress(p => p ? {
                ...p,
                done: true,
                currentSite: 'Connection lost — batch may have partially completed',
              } : p);
              eventSource.close();
              batchEventSourceRef.current = null;
              void reload();
            };
          }}
          onCancel={() => {
            if (batchId) {
              void api.cancelBatch(projectId!, batchId);
            }
          }}
          onClose={() => {
            setShowBatchModal(false);
            setBatchProgress(null);
            setBatchId(null);
          }}
        />
      )}
    </div>
  );
}

// ──────────────────────────── subcomponents ────────────────────────────

function PageHeader({ projectId }: { projectId: string | undefined }) {
  return (
    <div className="flex items-center justify-between">
      <h1 className="text-2xl font-semibold text-slate-800 dark:text-slate-200">
        Stitch Review
      </h1>
      <div className="flex items-center gap-4">
        {/* #963 track 3: sibling URL for the click→fetch layer. */}
        <Link
          to={`/projects/${projectId}/click-stitch-review`}
          className="text-sm text-blue-600 hover:underline"
        >
          Click stitch review →
        </Link>
        <Link
          to={`/projects/${projectId}/flows`}
          className="text-sm text-blue-600 hover:underline"
        >
          ← Back to E2E Flows
        </Link>
      </div>
    </div>
  );
}

/**
 * Issue #885: card for a site in the unresolvable bucket. Renders the
 * call site + a reason explanation; exposes the same three manual
 * actions as the review queue (pick a route, mark external, suggest
 * with AI) but no ranked candidates — by definition the tracer has no
 * URL to compare against routes.
 */
function UnresolvableCard({
  site,
  reason,
  busy,
  onPickRoute,
  onIgnore,
  onAskAI,
}: {
  site: StitchApiCallSite;
  reason: 'dynamic' | 'traced-via-call-graph' | 'env-var';
  busy: boolean;
  onPickRoute: () => void;
  onIgnore: () => void;
  onAskAI: () => void;
}) {
  const reasonCopy = {
    dynamic:
      'The URL argument is a dynamic expression the static tracer couldn\u2019t see through (e.g. `fetch(cfg.url)` or `fetch(getEndpoint())`). AI or manual review can usually resolve it.',
    'traced-via-call-graph':
      'primitiveTracer reached this caller while walking back from a fetch sink, but the endpoint couldn\u2019t be propagated through the chain. The actual URL is typically visible in the wrapper\u2019s implementation or its callers.',
    'env-var':
      'The URL uses an ALL-CAPS env-var template variable (e.g. `${API_BASE}`) that the static tracer refuses to resolve. The literal path is usually still determinable from context.',
  }[reason];

  const reasonLabel = {
    dynamic: 'Dynamic URL',
    'traced-via-call-graph': 'Traced via call graph',
    'env-var': 'Env-var URL',
  }[reason];

  return (
    <div
      data-testid="unresolvable-card"
      className="border border-slate-200 dark:border-slate-700 rounded bg-slate-50 dark:bg-slate-800/40 p-4"
    >
      <div className="flex items-start justify-between gap-3 mb-2">
        <div className="min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span className="inline-block px-1.5 py-0.5 rounded bg-slate-200 dark:bg-slate-700 text-xs">
              {site.method}
            </span>
            <span className="font-mono text-sm break-all text-slate-700 dark:text-slate-300">
              {site.urlPattern}
            </span>
          </div>
          <div className="text-xs text-slate-500">
            <span className="font-mono">
              {prettyPath(site.callSiteKey.filePath)}
            </span>
            {site.callSiteKey.enclosingSymbol && (
              <>
                {' \u00b7 '}
                <span className="font-mono">
                  {site.callSiteKey.enclosingSymbol}
                </span>
              </>
            )}
          </div>
        </div>
        <span
          data-testid="unresolvable-reason"
          className="flex-shrink-0 px-2 py-0.5 rounded text-[10px] font-medium bg-slate-200 dark:bg-slate-700 text-slate-600 dark:text-slate-300"
        >
          {reasonLabel}
        </span>
      </div>
      <p className="text-xs text-slate-600 dark:text-slate-400 mb-3">
        {reasonCopy}
      </p>
      <div className="flex gap-2">
        <button
          data-testid="unresolvable-pick-route"
          onClick={onPickRoute}
          disabled={busy}
          className="px-2 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
        >
          Pick route manually
        </button>
        <button
          data-testid="unresolvable-mark-external"
          onClick={onIgnore}
          disabled={busy}
          className="px-2 py-1 text-xs bg-slate-200 dark:bg-slate-700 text-slate-700 dark:text-slate-300 rounded hover:bg-slate-300 dark:hover:bg-slate-600 disabled:opacity-50"
        >
          Mark external
        </button>
        <button
          data-testid="unresolvable-suggest-ai"
          onClick={onAskAI}
          disabled={busy}
          className="px-2 py-1 text-xs bg-purple-600 text-white rounded hover:bg-purple-700 disabled:opacity-50"
        >
          Suggest with AI
        </button>
      </div>
    </div>
  );
}

// SummaryCard extracted to apps/web/src/components/stitching/SummaryCard.tsx
// (#974 review: shared across StitchReviewPage and ClickStitchReviewPage
// so the two pages can't drift visually).

function ReviewCard({
  row,
  busy,
  onAccept,
  onReject,
  onChange,
  onIgnore,
  onAskAI,
}: {
  row: ReviewRow;
  busy: boolean;
  onAccept: (c: StitchCandidateExplanation) => void;
  onReject: (c: StitchCandidateExplanation) => void;
  onChange: () => void;
  onIgnore: () => void;
  onAskAI: () => void;
}) {
  const bucketColor =
    row.bucket === 'ambiguous'
      ? 'border-amber-300 dark:border-amber-700 bg-amber-50/40 dark:bg-amber-900/10'
      : 'border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800/40';

  return (
    <div
      data-testid="review-card"
      data-call-site-id={row.site.callSiteId}
      className={`border ${bucketColor} rounded-lg overflow-hidden`}
    >
      <div className="grid grid-cols-1 md:grid-cols-2 divide-y md:divide-y-0 md:divide-x divide-slate-200 dark:divide-slate-700">
        {/* ─── Left: client call ─── */}
        <div className="p-4">
          <div className="flex items-center gap-2 mb-2">
            <span className="text-xs uppercase font-semibold text-slate-500">
              Client call
            </span>
            <span
              className={`text-xs px-1.5 py-0.5 rounded ${
                row.bucket === 'ambiguous'
                  ? 'bg-amber-100 dark:bg-amber-900/50 text-amber-700 dark:text-amber-300'
                  : 'bg-red-100 dark:bg-red-900/50 text-red-700 dark:text-red-300'
              }`}
            >
              {row.bucket}
            </span>
          </div>
          <div className="font-mono text-sm text-slate-800 dark:text-slate-200 break-all">
            <span className="inline-block px-1.5 py-0.5 rounded bg-slate-200 dark:bg-slate-700 text-xs mr-2">
              {row.site.method}
            </span>
            {row.site.urlPattern}
          </div>
          <div className="text-xs text-slate-500 mt-2">
            <div>
              <span className="text-slate-400">file:</span>{' '}
              <span className="font-mono">
                {prettyPath(row.site.filePath)}:{row.site.lineStart}
              </span>
            </div>
            <div>
              <span className="text-slate-400">fn:</span>{' '}
              <span className="font-mono">
                {row.site.callSiteKey.enclosingSymbol}
              </span>
            </div>
            {row.site.wrapperChain.length > 0 && (
              <div>
                <span className="text-slate-400">chain:</span>{' '}
                <span className="font-mono">
                  {row.site.wrapperChain.join(' → ')}
                </span>
              </div>
            )}
            {row.site.library && (
              <div>
                <span className="text-slate-400">lib:</span>{' '}
                <span className="font-mono">{row.site.library}</span>
              </div>
            )}
          </div>
          {row.ambiguityNote && (
            <div className="mt-2 text-xs text-amber-700 dark:text-amber-300">
              {row.ambiguityNote}
            </div>
          )}
          <div className="mt-3 flex gap-2">
            <button
              data-testid="change-button"
              onClick={onChange}
              disabled={busy}
              className="px-2 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
            >
              Change…
            </button>
            <button
              data-testid="mark-external-button"
              onClick={onIgnore}
              disabled={busy}
              className="px-2 py-1 text-xs bg-slate-200 dark:bg-slate-700 text-slate-700 dark:text-slate-300 rounded hover:bg-slate-300 dark:hover:bg-slate-600 disabled:opacity-50"
            >
              Mark external
            </button>
            <button
              data-testid="suggest-ai-button"
              onClick={onAskAI}
              disabled={busy}
              className="px-2 py-1 text-xs bg-purple-600 text-white rounded hover:bg-purple-700 disabled:opacity-50"
            >
              Suggest with AI
            </button>
          </div>
        </div>

        {/* ─── Right: candidate bindings ─── */}
        <div className="p-4">
          <div className="text-xs uppercase font-semibold text-slate-500 mb-2">
            Candidate bindings
          </div>
          {row.candidates.length === 0 ? (
            <div className="text-sm text-slate-500">
              No plausible candidate routes. Use{' '}
              <em className="not-italic font-medium">Change…</em> to pick from
              all routes, or <em className="not-italic font-medium">Suggest with AI</em>.
            </div>
          ) : (
            <ul className="space-y-3">
              {row.candidates.map((c, idx) => (
                <li
                  data-testid="candidate-row"
                  key={c.route.routeId}
                  className="border border-slate-200 dark:border-slate-700 rounded p-2.5 bg-white dark:bg-slate-900/30"
                >
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <div className="font-mono text-sm text-slate-800 dark:text-slate-200 break-all">
                        <span className="inline-block px-1.5 py-0.5 rounded bg-slate-200 dark:bg-slate-700 text-xs mr-2">
                          {c.route.method}
                        </span>
                        {c.route.path}
                      </div>
                      <div className="text-xs text-slate-500 mt-0.5 truncate">
                        {c.route.framework} · {c.route.handlerFunction ?? 'anonymous'}
                        {' · '}
                        <span className="font-mono">
                          {prettyPath(c.route.routeKey.filePath)}
                        </span>
                      </div>
                    </div>
                    <div className="flex gap-1">
                      <button
                        data-testid="accept-button"
                        onClick={() => onAccept(c)}
                        disabled={busy}
                        className={`px-2 py-1 text-xs rounded disabled:opacity-50 ${
                          idx === 0
                            ? 'bg-green-600 text-white hover:bg-green-700'
                            : 'bg-slate-200 dark:bg-slate-700 text-slate-800 dark:text-slate-200 hover:bg-slate-300 dark:hover:bg-slate-600'
                        }`}
                      >
                        Accept
                      </button>
                      <button
                        data-testid="reject-button"
                        onClick={() => onReject(c)}
                        disabled={busy}
                        className="px-2 py-1 text-xs text-slate-500 hover:text-red-600 disabled:opacity-50"
                      >
                        Reject
                      </button>
                    </div>
                  </div>
                  <div className="mt-1.5 flex flex-wrap gap-1.5 text-xs">
                    <Chip tone="slate">score {c.score}</Chip>
                    {c.breakdown.exactMatch && <Chip tone="green">exact</Chip>}
                    {!c.breakdown.exactMatch && c.breakdown.suffixMatch && (
                      <Chip tone="blue">suffix</Chip>
                    )}
                    <Chip tone={c.breakdown.methodMatch ? 'green' : 'red'}>
                      {c.breakdown.methodMatch ? 'method ✓' : 'method ✗'}
                    </Chip>
                    {c.breakdown.pathOverlap > 0 && (
                      <Chip tone="slate">
                        {c.breakdown.pathOverlap} segment
                        {c.breakdown.pathOverlap === 1 ? '' : 's'}
                      </Chip>
                    )}
                    {c.breakdown.pathLengthDiff > 0 && (
                      <Chip tone="amber">±{c.breakdown.pathLengthDiff} len</Chip>
                    )}
                  </div>
                  <div className="mt-1 text-xs text-slate-500 italic">
                    {c.rationale}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}

function Chip({
  children,
  tone,
}: {
  children: React.ReactNode;
  tone: 'slate' | 'green' | 'blue' | 'amber' | 'red';
}) {
  const map = {
    slate:
      'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 border-slate-200 dark:border-slate-700',
    green:
      'bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-300 border-green-200 dark:border-green-800',
    blue:
      'bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 border-blue-200 dark:border-blue-800',
    amber:
      'bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-300 border-amber-200 dark:border-amber-800',
    red:
      'bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 border-red-200 dark:border-red-800',
  };
  return (
    <span
      className={`inline-flex items-center px-1.5 py-0.5 rounded border ${map[tone]}`}
    >
      {children}
    </span>
  );
}

function RouteBrowserModal({
  site,
  routes,
  filter,
  onFilterChange,
  onClose,
  onPick,
}: {
  site: StitchApiCallSite;
  routes: StitchRouteEndpoint[];
  filter: string;
  onFilterChange: (s: string) => void;
  onClose: () => void;
  onPick: (r: StitchRouteEndpoint) => void;
}) {
  return (
    <div
      className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-50"
      onClick={onClose}
    >
      <div
        className="bg-white dark:bg-slate-800 rounded-lg shadow-xl max-w-2xl w-full max-h-[80vh] overflow-hidden flex flex-col"
        onClick={e => e.stopPropagation()}
      >
        <div className="p-4 border-b border-slate-200 dark:border-slate-700">
          <div className="text-sm font-semibold text-slate-700 dark:text-slate-200">
            Change binding for {site.method} {site.urlPattern}
          </div>
          <input
            value={filter}
            onChange={e => onFilterChange(e.target.value)}
            placeholder="Filter routes…"
            className="mt-2 w-full px-3 py-1.5 text-sm border border-slate-300 dark:border-slate-600 rounded bg-white dark:bg-slate-900"
            autoFocus
          />
        </div>
        <ul className="flex-1 overflow-auto divide-y divide-slate-100 dark:divide-slate-700">
          {routes.map(route => (
            <li
              key={route.routeId}
              className="p-3 hover:bg-slate-50 dark:hover:bg-slate-700/50 cursor-pointer"
              onClick={() => onPick(route)}
            >
              <div className="font-mono text-sm">
                <span className="inline-block px-1.5 py-0.5 rounded bg-slate-200 dark:bg-slate-700 text-xs mr-2">
                  {route.method}
                </span>
                {route.path}
              </div>
              <div className="text-xs text-slate-500 mt-0.5">
                {route.framework} · {route.handlerFunction ?? 'anonymous'}
              </div>
            </li>
          ))}
          {routes.length === 0 && (
            <li className="p-4 text-sm text-slate-500 text-center">
              No matching routes.
            </li>
          )}
        </ul>
      </div>
    </div>
  );
}

function CreateRuleDialog({
  form,
  setForm,
  error,
  onCancel,
  onSubmit,
}: {
  form: CreateRuleForm;
  setForm: (f: CreateRuleForm) => void;
  error: string | null;
  onCancel: () => void;
  onSubmit: () => void;
}) {
  return (
    <div
      className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-50"
      onClick={onCancel}
    >
      <div
        className="bg-white dark:bg-slate-800 rounded-lg shadow-xl max-w-xl w-full p-5"
        onClick={e => e.stopPropagation()}
      >
        <h3 className="text-lg font-semibold text-slate-800 dark:text-slate-200 mb-1">
          Create stitch rule
        </h3>
        <p className="text-xs text-slate-500 mb-4">
          Rules are evaluated in order after exact and parameterized matches.
          They persist to <code>.veodiagram/stitches.json</code> and apply on
          re-analysis.
        </p>

        <div className="grid grid-cols-2 gap-3 text-sm">
          <label className="col-span-1">
            <div className="text-xs font-medium text-slate-600 dark:text-slate-300 mb-1">
              Match on
            </div>
            <select
              value={form.matchKind}
              onChange={e =>
                setForm({ ...form, matchKind: e.target.value as CreateRuleForm['matchKind'] })
              }
              className="w-full px-2 py-1.5 border border-slate-300 dark:border-slate-600 rounded bg-white dark:bg-slate-900"
            >
              <option value="wrapperChain">wrapperChain (glob)</option>
              <option value="urlPattern">urlPattern (substring)</option>
              <option value="enclosingSymbol">enclosingSymbol (glob)</option>
            </select>
          </label>
          <label className="col-span-1">
            <div className="text-xs font-medium text-slate-600 dark:text-slate-300 mb-1">
              Match pattern
            </div>
            <input
              value={form.matchPattern}
              onChange={e => setForm({ ...form, matchPattern: e.target.value })}
              placeholder="e.g. *apiClient.users*"
              className="w-full px-2 py-1.5 border border-slate-300 dark:border-slate-600 rounded bg-white dark:bg-slate-900 font-mono text-xs"
            />
          </label>

          <label className="col-span-1">
            <div className="text-xs font-medium text-slate-600 dark:text-slate-300 mb-1">
              Target kind
            </div>
            <select
              value={form.targetKind}
              onChange={e =>
                setForm({ ...form, targetKind: e.target.value as CreateRuleForm['targetKind'] })
              }
              className="w-full px-2 py-1.5 border border-slate-300 dark:border-slate-600 rounded bg-white dark:bg-slate-900"
            >
              <option value="exact">exact path</option>
              <option value="pathSuffix">path suffix</option>
              <option value="regex">regex (advanced)</option>
            </select>
          </label>
          <label className="col-span-1">
            <div className="text-xs font-medium text-slate-600 dark:text-slate-300 mb-1">
              Target pattern
            </div>
            <input
              value={form.targetPattern}
              onChange={e => setForm({ ...form, targetPattern: e.target.value })}
              placeholder="/api/users/:id"
              className="w-full px-2 py-1.5 border border-slate-300 dark:border-slate-600 rounded bg-white dark:bg-slate-900 font-mono text-xs"
            />
          </label>

          <label className="col-span-1">
            <div className="text-xs font-medium text-slate-600 dark:text-slate-300 mb-1">
              Method (optional)
            </div>
            <input
              value={form.targetMethod}
              onChange={e => setForm({ ...form, targetMethod: e.target.value })}
              placeholder="GET / POST / …"
              className="w-full px-2 py-1.5 border border-slate-300 dark:border-slate-600 rounded bg-white dark:bg-slate-900 font-mono text-xs"
            />
          </label>
          <label className="col-span-1">
            <div className="text-xs font-medium text-slate-600 dark:text-slate-300 mb-1">
              Confidence
            </div>
            <input
              type="number"
              min={0}
              max={1}
              step={0.05}
              value={form.confidence}
              onChange={e => setForm({ ...form, confidence: Number(e.target.value) })}
              className="w-full px-2 py-1.5 border border-slate-300 dark:border-slate-600 rounded bg-white dark:bg-slate-900 font-mono text-xs"
            />
          </label>

          <label className="col-span-2">
            <div className="text-xs font-medium text-slate-600 dark:text-slate-300 mb-1">
              Note (optional)
            </div>
            <input
              value={form.note}
              onChange={e => setForm({ ...form, note: e.target.value })}
              placeholder="Why this rule exists"
              className="w-full px-2 py-1.5 border border-slate-300 dark:border-slate-600 rounded bg-white dark:bg-slate-900"
            />
          </label>
        </div>

        {error && (
          <div className="mt-3 text-xs text-red-600 dark:text-red-400">{error}</div>
        )}

        <div className="mt-5 flex justify-end gap-2">
          <button
            onClick={onCancel}
            className="px-3 py-1.5 text-sm bg-slate-200 dark:bg-slate-700 text-slate-700 dark:text-slate-200 rounded hover:bg-slate-300 dark:hover:bg-slate-600"
          >
            Cancel
          </button>
          <button
            onClick={onSubmit}
            className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded hover:bg-blue-700"
          >
            Create
          </button>
        </div>
      </div>
    </div>
  );
}

function AIProposeModal({
  site,
  preview,
  loading,
  sending,
  proposals,
  resultText,
  accepted,
  error,
  onClose,
  onSend,
  onAccept,
  onReject,
}: {
  site: StitchApiCallSite;
  preview: StitchPromptPreview | null;
  loading: boolean;
  sending: boolean;
  proposals: StitchEntry[] | null;
  resultText: string | null;
  accepted: boolean;
  error: string | null;
  onClose: () => void;
  onSend: () => void;
  onAccept: (proposal: StitchEntry) => void;
  onReject: () => void;
}) {
  const hasResult = proposals !== null || resultText !== null;
  return (
    <div
      className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-50"
      onClick={onClose}
    >
      <div
        data-testid="ai-modal"
        className="bg-white dark:bg-slate-800 rounded-lg shadow-xl max-w-3xl w-full max-h-[85vh] overflow-hidden flex flex-col"
        onClick={e => e.stopPropagation()}
      >
        <div className="p-4 border-b border-slate-200 dark:border-slate-700">
          <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-200">
            AI stitch proposal
          </h3>
          <p className="text-xs text-slate-500 mt-0.5">
            For{' '}
            <span className="font-mono">
              {site.method} {site.urlPattern}
            </span>
            {preview?.model && (
              <span className="ml-2 text-slate-400">model: {preview.model}</span>
            )}
          </p>
        </div>

        <div className="flex-1 overflow-auto p-4 space-y-3">
          {loading && (
            <div className="text-sm text-slate-500">Loading prompt preview…</div>
          )}
          {preview && !loading && (
            <>
              <details className="text-xs">
                <summary className="cursor-pointer font-semibold text-slate-600 dark:text-slate-300 mb-1">
                  System prompt ({preview.systemPrompt.length} chars)
                </summary>
                <pre
                  data-testid="ai-system-prompt"
                  className="mt-1 p-2 bg-slate-100 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded overflow-x-auto font-mono text-[11px] whitespace-pre-wrap"
                >
                  {preview.systemPrompt}
                </pre>
              </details>
              <details className="text-xs">
                <summary className="cursor-pointer font-semibold text-slate-600 dark:text-slate-300 mb-1">
                  User message ({preview.userMessage.length} chars)
                </summary>
                <pre
                  data-testid="ai-user-message"
                  className="mt-1 p-2 bg-slate-100 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded overflow-x-auto font-mono text-[11px] whitespace-pre-wrap"
                >
                  {preview.userMessage}
                </pre>
              </details>
            </>
          )}

          {/* ─── AI proposal result ─── */}
          {proposals && proposals.length > 0 && !accepted && (
            <div data-testid="ai-result" className="space-y-3">
              <div className="text-xs font-semibold text-green-700 dark:text-green-300 uppercase">
                AI suggests {proposals.length} binding{proposals.length === 1 ? '' : 's'}
              </div>
              {proposals.map((p, i) => (
                <div
                  key={i}
                  data-testid="ai-proposal-card"
                  className="border border-green-200 dark:border-green-800 bg-green-50 dark:bg-green-900/20 rounded overflow-hidden"
                >
                  {/* ─── Side-by-side: caller → matched endpoint ─── */}
                  <div className="grid grid-cols-1 md:grid-cols-[1fr_auto_1fr] gap-0 divide-y md:divide-y-0 md:divide-x divide-green-200 dark:divide-green-800">
                    {/* Caller side */}
                    <div className="p-3">
                      <div className="text-[10px] uppercase font-semibold text-slate-500 mb-1">
                        Client call
                      </div>
                      <div className="font-mono text-sm text-slate-800 dark:text-slate-200">
                        <span className="inline-block px-1.5 py-0.5 rounded bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 text-xs mr-1">
                          {site.method}
                        </span>
                        {site.urlPattern}
                      </div>
                      <div className="text-xs text-slate-500 mt-1 font-mono">
                        {site.callSiteKey.enclosingSymbol} ·{' '}
                        {(site.filePath ?? '').split('/').slice(-2).join('/')}:{site.lineStart}
                      </div>
                    </div>

                    {/* Arrow */}
                    <div className="hidden md:flex items-center justify-center px-2 text-green-600 dark:text-green-400">
                      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                      </svg>
                    </div>

                    {/* Matched endpoint side */}
                    <div className="p-3">
                      <div className="text-[10px] uppercase font-semibold text-green-700 dark:text-green-300 mb-1">
                        AI suggests this endpoint
                      </div>
                      <div className="font-mono text-sm text-slate-800 dark:text-slate-200">
                        <span className="inline-block px-1.5 py-0.5 rounded bg-green-200 dark:bg-green-800 text-green-800 dark:text-green-200 text-xs mr-1">
                          {p.route.method}
                        </span>
                        {p.route.path}
                      </div>
                      <div className="text-xs text-slate-500 mt-1">
                        {p.route.framework} · {p.route.handlerSymbol} ·{' '}
                        <span className="font-mono">
                          {(p.route.filePath ?? '').split('/').slice(-2).join('/')}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* ─── Confidence + rationale + actions ─── */}
                  <div className="px-3 py-2 border-t border-green-200 dark:border-green-800 flex items-center justify-between gap-3">
                    <div className="flex items-center gap-3 text-xs flex-1 min-w-0">
                      <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300 border border-green-200 dark:border-green-800 shrink-0">
                        confidence {(p.confidence * 100).toFixed(0)}%
                      </span>
                      <span className="italic text-slate-600 dark:text-slate-400 truncate">
                        {p.rationale}
                      </span>
                    </div>
                    <div className="flex gap-1.5 shrink-0">
                      <button
                        data-testid="ai-accept"
                        onClick={() => onAccept(p)}
                        className="px-3 py-1.5 text-xs font-medium bg-green-600 text-white rounded hover:bg-green-700"
                      >
                        Accept
                      </button>
                      <button
                        data-testid="ai-reject"
                        onClick={onReject}
                        className="px-3 py-1.5 text-xs font-medium bg-slate-200 dark:bg-slate-700 text-slate-700 dark:text-slate-300 rounded hover:bg-slate-300 dark:hover:bg-slate-600"
                      >
                        Reject
                      </button>
                    </div>
                  </div>

                  {/* ─── AI reasoning (collapsible) ─── */}
                  {p.aiReasoning && (
                    <div className="px-3 pb-3">
                      <details className="text-xs">
                        <summary className="cursor-pointer font-semibold text-slate-600 dark:text-slate-300">
                          AI reasoning
                        </summary>
                        <pre
                          data-testid="ai-reasoning"
                          className="mt-1 p-2 bg-slate-100 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded overflow-x-auto font-mono text-[11px] whitespace-pre-wrap text-slate-700 dark:text-slate-300"
                        >
                          {p.aiReasoning}
                        </pre>
                      </details>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
          {accepted && (
            <div
              data-testid="ai-result"
              className="p-3 border border-green-200 dark:border-green-800 bg-green-50 dark:bg-green-900/20 text-green-800 dark:text-green-300 text-sm rounded"
            >
              Binding accepted and saved. The call site has been resolved.
            </div>
          )}
          {resultText && (
            <div
              data-testid="ai-result"
              className="p-3 border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20 text-amber-800 dark:text-amber-300 text-sm rounded"
            >
              {resultText}
            </div>
          )}
          {error && (
            <div className="p-3 border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20 text-red-800 dark:text-red-300 text-sm rounded">
              {error}
            </div>
          )}
        </div>

        <div className="p-4 border-t border-slate-200 dark:border-slate-700 flex justify-end gap-2">
          <button
            data-testid="ai-cancel"
            onClick={onClose}
            className="px-3 py-1.5 text-sm bg-slate-200 dark:bg-slate-700 text-slate-700 dark:text-slate-200 rounded hover:bg-slate-300 dark:hover:bg-slate-600"
          >
            {hasResult || accepted ? 'Close' : 'Cancel'}
          </button>
          {!hasResult && !accepted && (
            <button
              data-testid="ai-send"
              onClick={onSend}
              disabled={loading || sending || !preview}
              className="px-3 py-1.5 text-sm bg-purple-600 text-white rounded hover:bg-purple-700 disabled:opacity-50"
            >
              {sending
                ? `Sending to ${preview?.model ?? 'AI'}…`
                : `Send to ${preview?.model ?? 'AI'}`}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ──────────────────────── Issue #827: Confident stitch row ────────────────────────

function ConfidentStitchRow({
  stitch,
  onRebind,
  onUnbind,
}: {
  stitch: StitchEntry;
  onRebind: () => void;
  onUnbind: () => void;
}) {
  // Issue #828: inline reasoning expand state.
  // Show the expandable rationale for AI stitches AND for user stitches
  // that were originally AI-proposed (their rationale starts with
  // "AI-proposed"). These carry the AI's reasoning even though their
  // source was changed to 'user' on acceptance.
  const [reasoningExpanded, setReasoningExpanded] = useState(false);
  const isAI = stitch.source === 'ai';
  const isAIDerived =
    isAI || (stitch.rationale ?? '').startsWith('AI-proposed');
  // Every stitch with a rationale gets the ... expand so the user
  // can always read the full untruncated text. AI-derived stitches
  // additionally show the reasoning panel.
  const hasExpandableContent = !!(stitch.rationale);

  const sourceBadge = {
    auto: { label: 'auto', bg: 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300' },
    user: { label: 'user', bg: 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300' },
    ai: { label: 'ai', bg: 'bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300' },
  }[stitch.source] ?? { label: stitch.source, bg: 'bg-slate-100 text-slate-700' };

  return (
    <div
      data-testid="confident-stitch-row"
      className="border border-slate-200 dark:border-slate-700 rounded-lg overflow-hidden bg-white dark:bg-slate-800/40"
    >
      {/* ─── Caller → Endpoint ─── */}
      <div className="grid grid-cols-1 md:grid-cols-[1fr_auto_1fr] divide-y md:divide-y-0 md:divide-x divide-slate-200 dark:divide-slate-700">
        <div className="p-3">
          <div className="text-[10px] uppercase font-semibold text-slate-500 mb-1">
            Client call
          </div>
          <div className="font-mono text-sm text-slate-800 dark:text-slate-200 break-all">
            <span className="inline-block px-1.5 py-0.5 rounded bg-slate-200 dark:bg-slate-700 text-xs mr-1">
              {stitch.callSite.method}
            </span>
            {stitch.callSite.urlPattern}
          </div>
          <div className="text-xs text-slate-500 mt-1 font-mono">
            {stitch.callSite.enclosingSymbol} ·{' '}
            {prettyPath(stitch.callSite.filePath ?? '')}
          </div>
        </div>

        <div className="hidden md:flex items-center justify-center px-2 text-green-600 dark:text-green-400">
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
          </svg>
        </div>

        <div className="p-3">
          <div className="text-[10px] uppercase font-semibold text-green-700 dark:text-green-300 mb-1">
            Bound endpoint
          </div>
          <div className="font-mono text-sm text-slate-800 dark:text-slate-200 break-all">
            <span className="inline-block px-1.5 py-0.5 rounded bg-green-100 dark:bg-green-800 text-green-800 dark:text-green-200 text-xs mr-1">
              {stitch.route.method}
            </span>
            {stitch.route.path}
          </div>
          <div className="text-xs text-slate-500 mt-1">
            {stitch.route.framework} · {stitch.route.handlerSymbol} ·{' '}
            <span className="font-mono">{prettyPath(stitch.route.filePath ?? '')}</span>
          </div>
        </div>
      </div>

      {/* ─── Provenance bar ─── */}
      <div className="px-3 py-2 border-t border-slate-200 dark:border-slate-700 flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2 text-xs flex-wrap">
          <span
            data-testid="source-badge"
            className={`inline-block px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase ${sourceBadge.bg}`}
          >
            {sourceBadge.label}
          </span>
          <span className="text-slate-500">
            confidence {(stitch.confidence * 100).toFixed(0)}%
          </span>
          {/* Issue #828: clickable rationale — AI stitches show ... to expand reasoning */}
          {hasExpandableContent ? (
            <button
              data-testid="reasoning-toggle"
              onClick={() => setReasoningExpanded(e => !e)}
              className={`italic text-slate-600 dark:text-slate-400 hover:text-primary transition-colors text-left ${
                reasoningExpanded ? '' : 'truncate max-w-[300px]'
              }`}
              title={reasoningExpanded ? 'Collapse' : 'Click to see full rationale and AI reasoning'}
            >
              {stitch.rationale}
              <span className="ml-1 not-italic text-primary/70">
                {reasoningExpanded ? '▾' : '…'}
              </span>
            </button>
          ) : (
            <span className="italic text-slate-600 dark:text-slate-400 truncate max-w-[300px]">
              {stitch.rationale}
            </span>
          )}
          {stitch.createdAt && (
            <span className="text-slate-400">
              {new Date(stitch.createdAt).toLocaleDateString()}
            </span>
          )}
          {stitch.createdBy && (
            <span className="text-slate-400">by {stitch.createdBy}</span>
          )}
        </div>
        <div className="flex gap-1.5 shrink-0">
          <button
            data-testid="rebind-button"
            onClick={onRebind}
            className="px-2 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700"
          >
            Rebind
          </button>
          {(stitch.source === 'user' || stitch.source === 'ai') && (
            <button
              data-testid="unbind-button"
              onClick={onUnbind}
              className="px-2 py-1 text-xs bg-slate-200 dark:bg-slate-700 text-slate-700 dark:text-slate-300 rounded hover:bg-slate-300 dark:hover:bg-slate-600"
            >
              Unbind
            </button>
          )}
        </div>
      </div>

      {/* ─── Issue #828: Expanded AI reasoning ─── */}
      {hasExpandableContent && reasoningExpanded && (
        <div
          data-testid="reasoning-panel"
          className="px-3 pb-3 border-t border-slate-100 dark:border-slate-700"
        >
          {/* Full rationale (untruncated) — shown for all stitches */}
          <div className="mt-2 mb-2">
            <div className="text-[10px] uppercase font-semibold text-slate-500 mb-0.5">
              Full rationale
            </div>
            <div
              data-testid="full-rationale"
              className="text-xs italic text-slate-700 dark:text-slate-300"
            >
              {stitch.rationale}
            </div>
          </div>
          {/* AI reasoning — only for AI-derived stitches */}
          {isAIDerived && (
            stitch.aiReasoning ? (
              <div>
                <div className="text-[10px] uppercase font-semibold text-purple-600 dark:text-purple-400 mb-1">
                  AI reasoning
                </div>
                <pre
                  data-testid="reasoning-content"
                  className="p-2 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded overflow-x-auto font-mono text-[11px] whitespace-pre-wrap text-slate-700 dark:text-slate-300 max-h-96 overflow-y-auto"
                >
                  {stitch.aiReasoning}
                </pre>
              </div>
            ) : (
              <div className="text-xs text-slate-500 italic">
                Full reasoning not available — this stitch was created before
                reasoning capture was enabled.
              </div>
            )
          )}
        </div>
      )}
    </div>
  );
}

// ──────────────────────── Issue #829: Batch resolve modal ────────────────────────

function BatchResolveModal({
  unresolvedCount,
  filterLabel = 'unresolved',
  batchProgress,
  batchResults,
  onStart,
  onCancel,
  onClose,
}: {
  unresolvedCount: number;
  filterLabel?: string;
  batchProgress: {
    current: number;
    total: number;
    proposed: number;
    declined: number;
    errors: number;
    belowThreshold: number;
    currentSite?: string;
    model?: string;
    done: boolean;
    cancelled: boolean;
    totalTokensUsed?: number;
  } | null;
  batchResults: BatchResult[];
  onStart: (opts: {
    confidenceThreshold: number;
    maxProposals: number;
    pacingDelayMs: number;
    includeAmbiguous: boolean;
  }) => void;
  onCancel: () => void;
  onClose: () => void;
}) {
  const [threshold, setThreshold] = useState(0.9);
  const [maxProposals, setMaxProposals] = useState(Math.min(unresolvedCount, 50));
  const [pacingDelay, setPacingDelay] = useState(3000);
  const [includeAmbiguous, setIncludeAmbiguous] = useState(false);

  const hasStarted = batchProgress !== null;
  const isDone = batchProgress?.done ?? false;

  return (
    <div
      className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-50"
      onClick={isDone ? onClose : undefined}
    >
      <div
        data-testid="batch-modal"
        className={`bg-white dark:bg-slate-800 rounded-lg shadow-xl w-full max-h-[85vh] overflow-hidden flex flex-col ${isDone && batchResults.length > 0 ? 'max-w-3xl' : 'max-w-lg'}`}
        onClick={e => e.stopPropagation()}
      >
        <div className="p-4 border-b border-slate-200 dark:border-slate-700">
          <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-200">
            Resolve all with AI
          </h3>
          <p className="text-xs text-slate-500 mt-0.5">
            {unresolvedCount} {filterLabel} call site{unresolvedCount === 1 ? '' : 's'} available
            {batchProgress?.model && ` · model: ${batchProgress.model}`}
          </p>
        </div>

        <div className="flex-1 overflow-auto p-4 space-y-4">
          {/* ─── Config (before start) ─── */}
          {!hasStarted && (
            <>
              <label className="flex items-center justify-between text-xs">
                <span>Confidence threshold</span>
                <div className="flex items-center gap-2">
                  <input
                    type="range"
                    min={0.5}
                    max={1}
                    step={0.05}
                    value={threshold}
                    onChange={e => setThreshold(Number(e.target.value))}
                    className="w-24"
                  />
                  <span className="font-mono w-10 text-right">{(threshold * 100).toFixed(0)}%</span>
                </div>
              </label>
              <label className="flex items-center justify-between text-xs">
                <span>Max proposals</span>
                <input
                  type="number"
                  min={1}
                  max={unresolvedCount}
                  value={maxProposals}
                  onChange={e => setMaxProposals(Number(e.target.value))}
                  className="w-20 px-2 py-1 border border-slate-300 dark:border-slate-600 rounded bg-white dark:bg-slate-900 font-mono text-right"
                />
              </label>
              <label className="flex items-center justify-between text-xs">
                <span>Pacing delay (ms between calls)</span>
                <input
                  type="number"
                  min={500}
                  max={10000}
                  step={500}
                  value={pacingDelay}
                  onChange={e => setPacingDelay(Number(e.target.value))}
                  className="w-20 px-2 py-1 border border-slate-300 dark:border-slate-600 rounded bg-white dark:bg-slate-900 font-mono text-right"
                />
              </label>
              <label className="flex items-center gap-2 text-xs">
                <input
                  type="checkbox"
                  checked={includeAmbiguous}
                  onChange={e => setIncludeAmbiguous(e.target.checked)}
                />
                Include ambiguous call sites
              </label>
              <div className="text-[11px] text-slate-500">
                Estimated time: ~{Math.ceil(maxProposals * ((pacingDelay + 15000) / 60000))} min
                (serial, {pacingDelay / 1000}s pacing + ~15s per LLM call)
              </div>
            </>
          )}

          {/* ─── Progress (during/after run) ─── */}
          {hasStarted && (
            <div data-testid="batch-progress" className="space-y-3">
              {/* Progress bar */}
              <div>
                <div className="flex justify-between text-xs text-slate-500 mb-1">
                  <span>
                    {isDone
                      ? batchProgress.cancelled
                        ? 'Batch stopped by user'
                        : 'Batch complete'
                      : `Processing ${batchProgress.current} of ${batchProgress.total}...`}
                  </span>
                  <span>{batchProgress.total > 0 ? Math.round((batchProgress.current / batchProgress.total) * 100) : 0}%</span>
                </div>
                <div className="h-2 bg-slate-200 dark:bg-slate-700 rounded overflow-hidden">
                  <div
                    className={`h-full transition-all duration-300 ${isDone ? 'bg-green-500' : 'bg-purple-500'}`}
                    style={{ width: `${batchProgress.total > 0 ? (batchProgress.current / batchProgress.total) * 100 : 0}%` }}
                  />
                </div>
              </div>

              {/* Current site */}
              {!isDone && batchProgress.currentSite && (
                <div className="text-xs text-slate-500 font-mono truncate">
                  {batchProgress.currentSite}
                </div>
              )}

              {/* Counters */}
              <div className="grid grid-cols-4 gap-2 text-xs">
                <div className="p-2 bg-green-50 dark:bg-green-900/20 rounded text-center">
                  <div className="text-lg font-semibold text-green-600" data-testid="batch-proposed">{batchProgress.proposed}</div>
                  <div className="text-green-600/70">Proposed</div>
                </div>
                <div className="p-2 bg-amber-50 dark:bg-amber-900/20 rounded text-center">
                  <div className="text-lg font-semibold text-amber-600">{batchProgress.belowThreshold}</div>
                  <div className="text-amber-600/70">Below threshold</div>
                </div>
                <div className="p-2 bg-slate-50 dark:bg-slate-800 rounded text-center">
                  <div className="text-lg font-semibold text-slate-600">{batchProgress.declined}</div>
                  <div className="text-slate-500">Declined</div>
                </div>
                <div className="p-2 bg-red-50 dark:bg-red-900/20 rounded text-center">
                  <div className="text-lg font-semibold text-red-600">{batchProgress.errors}</div>
                  <div className="text-red-600/70">Errors</div>
                </div>
              </div>

              {isDone && batchProgress.totalTokensUsed && (
                <div className="text-xs text-slate-500">
                  Total tokens used: {batchProgress.totalTokensUsed.toLocaleString()}
                </div>
              )}

              {/* #832: per-site results table once the batch completes. */}
              {isDone && batchResults.length > 0 && (
                <BatchResultsTable results={batchResults} />
              )}
            </div>
          )}
        </div>

        <div className="p-4 border-t border-slate-200 dark:border-slate-700 flex justify-end gap-2">
          {!hasStarted && (
            <>
              <button onClick={onClose} className="px-3 py-1.5 text-sm bg-slate-200 dark:bg-slate-700 text-slate-700 dark:text-slate-200 rounded hover:bg-slate-300 dark:hover:bg-slate-600">
                Cancel
              </button>
              <button
                data-testid="batch-start"
                onClick={() => onStart({ confidenceThreshold: threshold, maxProposals, pacingDelayMs: pacingDelay, includeAmbiguous })}
                className="px-3 py-1.5 text-sm bg-purple-600 text-white rounded hover:bg-purple-700"
              >
                Start batch
              </button>
            </>
          )}
          {hasStarted && !isDone && (
            <button
              data-testid="batch-stop"
              onClick={onCancel}
              className="px-3 py-1.5 text-sm bg-red-600 text-white rounded hover:bg-red-700"
            >
              Stop
            </button>
          )}
          {isDone && (
            <button
              data-testid="batch-close"
              onClick={onClose}
              className="px-3 py-1.5 text-sm bg-slate-200 dark:bg-slate-700 text-slate-700 dark:text-slate-200 rounded hover:bg-slate-300 dark:hover:bg-slate-600"
            >
              Close
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ──────────────────────── Issue #832: Batch results table ────────────────────────

/**
 * Per-site result detail with status filter chips + Copy as text/CSV.
 * Renders inside the BatchResolveModal after the batch completes.
 */
function BatchResultsTable({ results }: { results: BatchResult[] }) {
  const [filter, setFilter] = useState<'all' | BatchResultStatus>('all');
  const [copied, setCopied] = useState<string | null>(null);

  const rowsWithStatus = useMemo(
    () => results.map(r => ({ r, status: classifyBatchResult(r) })),
    [results],
  );

  const counts = useMemo(() => {
    const c = { all: rowsWithStatus.length, proposed: 0, 'below-threshold': 0, declined: 0, error: 0 };
    for (const { status } of rowsWithStatus) c[status]++;
    return c;
  }, [rowsWithStatus]);

  const visible = useMemo(() => {
    if (filter === 'all') return rowsWithStatus;
    return rowsWithStatus.filter(x => x.status === filter);
  }, [rowsWithStatus, filter]);

  const reasonText = (r: BatchResult, status: BatchResultStatus) => {
    if (status === 'proposed' && r.rationale) return r.rationale;
    if (status === 'below-threshold')
      return `confidence ${Math.round((r.confidence ?? 0) * 100)}% — ${r.rationale ?? r.reason ?? ''}`;
    if (r.detail) return r.detail;
    if (r.reason) return r.reason;
    return '';
  };

  const copyAsText = () => {
    const text = visible
      .map(({ r, status }) => `${status.padEnd(16)}  ${r.method.padEnd(6)} ${r.urlPattern}  —  ${reasonText(r, status)}`)
      .join('\n');
    navigator.clipboard?.writeText(text);
    setCopied('text');
    setTimeout(() => setCopied(null), 1500);
  };

  const copyAsCsv = () => {
    const esc = (s: string) => `"${s.replace(/"/g, '""')}"`;
    const header = ['status', 'method', 'urlPattern', 'confidence', 'reason', 'detail'].join(',');
    const rows = visible.map(({ r, status }) =>
      [status, r.method, r.urlPattern, r.confidence ?? '', r.reason ?? '', r.detail ?? r.rationale ?? '']
        .map(v => esc(String(v)))
        .join(','),
    );
    navigator.clipboard?.writeText([header, ...rows].join('\n'));
    setCopied('csv');
    setTimeout(() => setCopied(null), 1500);
  };

  const tones: Record<BatchResultStatus, string> = {
    proposed: 'bg-green-50 dark:bg-green-900/15 border-l-2 border-green-500',
    'below-threshold': 'bg-amber-50 dark:bg-amber-900/15 border-l-2 border-amber-500',
    declined: 'bg-slate-50 dark:bg-slate-800/40 border-l-2 border-slate-400',
    error: 'bg-red-50 dark:bg-red-900/15 border-l-2 border-red-500',
  };

  const chips: Array<{ key: 'all' | BatchResultStatus; label: string; tone: string }> = [
    { key: 'all', label: `All (${counts.all})`, tone: 'border-slate-400' },
    { key: 'proposed', label: `Proposed (${counts.proposed})`, tone: 'border-green-500 text-green-700 dark:text-green-300' },
    { key: 'below-threshold', label: `Below threshold (${counts['below-threshold']})`, tone: 'border-amber-500 text-amber-700 dark:text-amber-300' },
    { key: 'declined', label: `Declined (${counts.declined})`, tone: 'border-slate-500 text-slate-700 dark:text-slate-300' },
    { key: 'error', label: `Errors (${counts.error})`, tone: 'border-red-500 text-red-700 dark:text-red-300' },
  ];

  return (
    <div data-testid="batch-results-table" className="mt-4 pt-3 border-t border-slate-200 dark:border-slate-700">
      <div className="flex items-center justify-between gap-2 mb-2 flex-wrap">
        <div className="flex items-center gap-1 text-xs flex-wrap">
          {chips.map(c => (
            <button
              key={c.key}
              data-testid={`batch-filter-${c.key}`}
              onClick={() => setFilter(c.key)}
              className={`px-2 py-1 rounded border transition-colors ${
                filter === c.key ? `bg-slate-100 dark:bg-slate-700 ${c.tone}` : `text-slate-500 hover:bg-slate-50 dark:hover:bg-slate-800 border-slate-300 dark:border-slate-600`
              }`}
            >
              {c.label}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-1 text-xs">
          <button
            onClick={copyAsText}
            data-testid="batch-copy-text"
            className="px-2 py-1 rounded border border-slate-300 dark:border-slate-600 text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800"
          >
            {copied === 'text' ? 'Copied!' : 'Copy as text'}
          </button>
          <button
            onClick={copyAsCsv}
            data-testid="batch-copy-csv"
            className="px-2 py-1 rounded border border-slate-300 dark:border-slate-600 text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800"
          >
            {copied === 'csv' ? 'Copied!' : 'Copy as CSV'}
          </button>
        </div>
      </div>
      {visible.length === 0 ? (
        <div className="text-xs text-slate-500 italic py-3">No results in this filter.</div>
      ) : (
        <div className="max-h-80 overflow-auto border border-slate-200 dark:border-slate-700 rounded">
          <table className="w-full text-xs">
            <thead className="bg-slate-50 dark:bg-slate-800/60 sticky top-0">
              <tr className="text-left">
                <th className="px-2 py-1 font-medium text-slate-500">Status</th>
                <th className="px-2 py-1 font-medium text-slate-500">Call site</th>
                <th className="px-2 py-1 font-medium text-slate-500">Reason</th>
              </tr>
            </thead>
            <tbody>
              {visible.map(({ r, status }, i) => (
                <tr key={`${r.callSiteId}-${i}`} className={tones[status]} data-testid={`batch-result-row-${status}`}>
                  <td className="px-2 py-1 whitespace-nowrap align-top">
                    <span className="inline-block px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase">
                      {status === 'below-threshold' ? 'low conf' : status}
                    </span>
                  </td>
                  <td className="px-2 py-1 align-top">
                    <div className="font-mono break-all">
                      <span className="inline-block px-1 py-0.5 rounded bg-slate-200 dark:bg-slate-700 mr-1">{r.method}</span>
                      {r.urlPattern}
                    </div>
                  </td>
                  <td className="px-2 py-1 align-top text-slate-600 dark:text-slate-400 italic">
                    {reasonText(r, status) || '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
